/**
 * The bridge between the vendored reporter widget and this app.
 *
 * `public/bugkit.js` is a zero-dependency script loaded from `index.html`, on
 * purpose and ahead of the bundle — its console and failed-request buffers only
 * record what happens after it runs, so a report filed after a boot error still
 * carries the boot error. It is copied verbatim from the `bug-report-system`
 * skill and must stay that way: everything this product needs to say to it is
 * said here, through its documented config surface, so the file itself can be
 * replaced wholesale when the skill updates.
 *
 * What this module supplies:
 *
 *   - **`pageKey`** — the grouping axis. Left to itself the widget takes the
 *     last path segment, which on `/inbox/inq_01J…` is an inquiry id: every
 *     report from a record would land in its own single-item group and the
 *     Inbox would look bug-free. `PAGE_KEYS` maps the route to a stable key
 *     instead, and each key maps 1:1 to a source file — the table is in the
 *     project CLAUDE.md, and that is what makes triage searchless.
 *   - **the page picker** — the same keys, labelled, so a reporter who noticed
 *     the bug on one screen and filed it from another can say so.
 *   - **`extraContext`** — who was looking, in which theme, at what width.
 *   - **the triggers** — the FAB and ⌃⌥B, mounted only once there is a session
 *     to attribute a report to.
 *
 * Deliberately NOT used: `BugKit.refreshBadge()` and `BugKit.myReports()`. The
 * rail badge and the triage screen are React, read through `@/lib/queries`, and
 * a second polling loop writing into the DOM behind React's back would fight
 * the first one.
 */

import { useEffect } from 'react';

/* ────────────────────────────────────────────────────────────────────────────
   The widget's surface, as much of it as this app touches
   ──────────────────────────────────────────────────────────────────────────── */

export interface BugKitConfig {
  api: string;
  endpoint: string;
  credentials: RequestCredentials;
  fab: boolean;
  hotkey: string;
  appVersion: string;
  pages: { key: string; label: string }[];
  pageKey: () => string;
  extraContext: () => Record<string, unknown>;
}

export interface BugKitApi {
  init: (options: Partial<BugKitConfig>) => BugKitApi;
  open: (options?: { context?: Record<string, unknown> }) => void;
  mountTrigger: (element: Element | string) => void;
  config: BugKitConfig;
}

declare global {
  interface Window {
    BugKit?: BugKitApi;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   Route → page key
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Longest prefix wins, so `/settings/team` beats `/settings`. Order matters:
 * the first match is taken.
 *
 * Settings is split per pane rather than collapsed to one `settings` key,
 * because the nine panes are nine files and "a bug in Settings" is not an
 * address. Everything else is one screen, one file, one key.
 */
const PAGE_KEYS: ReadonlyArray<readonly [prefix: string, key: string, label: string]> = [
  ['/settings/profile', 'settings-profile', 'Settings · Profile'],
  ['/settings/notifications', 'settings-notifications', 'Settings · Notifications'],
  ['/settings/team', 'settings-team', 'Settings · Team'],
  ['/settings/pipeline', 'settings-pipeline', 'Settings · Pipeline'],
  ['/settings/tags', 'settings-tags', 'Settings · Tags'],
  ['/settings/views', 'settings-views', 'Settings · Saved views'],
  ['/settings/mail', 'settings-mail', 'Settings · Email'],
  ['/settings/api', 'settings-api', 'Settings · API & webhooks'],
  ['/settings/audit', 'settings-audit', 'Settings · Audit log'],
  ['/settings', 'settings', 'Settings'],
  ['/inbox', 'inbox', 'Inbox'],
  ['/board', 'board', 'Board'],
  ['/contacts', 'contacts', 'Contacts'],
  ['/companies', 'companies', 'Companies'],
  ['/tasks', 'tasks', 'Tasks'],
  ['/analytics', 'analytics', 'Analytics'],
  ['/getting-started', 'getting-started', 'Getting started'],
  ['/bugs', 'bugs', 'Bug reports'],
  ['/login', 'login', 'Sign in'],
];

/** The picker's options, in the rail's own order. */
const PAGES = PAGE_KEYS.map(([, key, label]) => ({ key, label }));

/**
 * Read at submit time, not at mount: the widget calls this when the reporter
 * presses send, so the key follows client-side navigation without this module
 * subscribing to the router.
 */
export function pageKeyFor(pathname: string): string {
  for (const [prefix, key] of PAGE_KEYS) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return key;
  }
  // An unrecognised key is not an error — it groups on its own until someone
  // adds it here, which is the whole point of the server accepting anything.
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment !== undefined ? segment.toLowerCase() : 'unknown';
}

/* ────────────────────────────────────────────────────────────────────────────
   Mount
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Configure the widget and mount its triggers. Called once from `AppShell`,
 * which only renders behind the session guard — so the FAB never appears on the
 * sign-in screen, where a submit could only 401.
 *
 * Takes the identity as two strings rather than an object: an object literal at
 * the call site is a new reference every render, and this effect would then
 * re-run on every keystroke anywhere in the shell.
 *
 * Re-running is harmless in any case — `init()` is idempotent about the FAB and
 * the hotkey, both of which check for an existing node before mounting.
 */
export function useBugKit(userId: string | null, role: string | null): void {
  useEffect(() => {
    if (userId === null) return;

    const mount = (): void => {
      const kit = window.BugKit;
      if (kit === undefined) return;

      kit.init({
        fab: true,
        hotkey: 'ctrl+alt+b',
        pages: PAGES,
        pageKey: () => pageKeyFor(window.location.pathname),
        extraContext: () => ({
          user_id: userId,
          role: role ?? 'unknown',
          theme: document.documentElement.dataset.theme ?? 'unknown',
          route: `${window.location.pathname}${window.location.search}`,
        }),
      });
    };

    // `bugkit.js` is deferred, so on a warm navigation it is already there and
    // on a cold load it is not. Both are normal; neither is worth a retry loop.
    if (window.BugKit !== undefined) mount();
    else window.addEventListener('DOMContentLoaded', mount, { once: true });

    return () => window.removeEventListener('DOMContentLoaded', mount);
  }, [userId, role]);
}

/** Open the reporter from the product's own UI — the account menu uses this. */
export function openBugReporter(): void {
  window.BugKit?.open();
}
